import ResourceVector from "./resource-vector.js";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const YEAR = 365 * DAY;

const SPEED = 1;

class Timing {
    // Calculates the required time for building 'level' in hours.
    static planet_getBuildingTimeForLevel(level) {
        if (level < 1) {
            return 0;
        }
        if (level < 10) {
            let res = {
                1: 0.3 / 60,
                2: 3.5 / 60,
                3: 10.3 / 60,
                4: 26.2 / 60,
                5: 62.5 / 60,
                6: 2.539,
                7: 4.036,
                8: 5.533,
                9: 7.03,
                10: 8, // Overlap
            }
            return res[level];
        } else {
            return Math.log(level - 7) / Math.log(1.3) + 3.9;
        }
    }

    // Calculate the speed-up/slow-down for building 'building_level' with 'central_level'
    static planet_getPlanetCenterSpeedUp(building_level, central_level) {
        if (central_level === 0) central_level = 1;
        central_level += 1;
        if (building_level === central_level) {
            return 1;
        }
        if (building_level < central_level) {
            let diff = central_level - building_level + 1;
            return (1 / (Math.pow(diff, 0.5)));
        } else {
            let diff = building_level - central_level + 1;
            return Math.pow(diff, 0.4);
        }
    }

    static combat_getTimeForCombatTick() {
        return Math.max(1, Math.ceil(10 * MINUTE / GameData.SPEED));
    }
}

class CostProductionFormulas {
    static getScaleFactor(level, base, level_offset = 0) {
        if (level < 1) {
            return 0;
        }
        return base * Math.pow(level + 5 + level_offset, 2);
    }
}

const RESOURCES = [
    {
        id: "worker",
        ui_name: "Worker",
        description: "TBA",
    }, {
        id: "concrete",
        ui_name: "Concrete",
        description: "Old but very useful for building construction.",
    }, {
        id: "metal",
        ui_name: "Metal",
        description: "Very hard. Can be extracted in metal mines.",
    }, {
        id: "plastic",
        ui_name: "Polymers",
        description: "A flexible set of materials ranging from classical plastic to biological compounds.",
    }, {
        id: "fuel",
        ui_name: "Fuel",
        description: "Some people call it rocket food.",
    },
];

// l := level, p := planet
const BUILDINGS = [
    {
        id: "central",
        ui_name: "Headquarters",
        description: "Your seat of government and center of operations. It ensures a basic resource production and can never be fully dismantled by sabotage. Upgrading it reduces construction time of all buildings.",
        ui_category: "infrastructure",
        cost: () => ({ worker: 4, concrete: 9, metal: 5, plastic: 3 }),
        time: () => (1.2),
        points: (l) => l * 2,
        energy: 12,
        cost_level_offset: 2,
        production: (l) => (l === 0 ? {} : { concrete: 5, metal: 5, plastic: 3 }),
        energy_production_factor: 3,
        energy_production_is_const: true,
        worker_production_const: 10,
        disable_production_scaling: true,
        requirements: [],
    }, {
        id: "housing",
        ui_name: "Living Complex",
        description: "Increases the amount of workers that will settle on this planet. Workers are required to operate factories, research and other facilities efficiently. Workers reproduce on their own: For every 20 workers on a planet, one new worker is created every hour.",
        ui_category: "infrastructure",
        cost: () => ({ worker: 0, concrete: 30, metal: 23, plastic: 15 }),
        time: () => (0.8),
        points: (l) => l * 1,
        worker_replication_factor: 0.05,
        energy: 5,
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "solar",
        ui_name: "Solar Plant",
        description: "Produces energy for the other buildings on this planet. Energy is needed to operate factories, research and other facilities efficiently. The amount of energy depends on the planet's star and it's distance.",
        ui_category: "infrastructure",
        cost: () => ({ worker: 3, concrete: 15, metal: 25, plastic: 35 }),
        time: () => (0.8),
        points: (l) => l * 1,
        energy_production_factor: 10,
        energy_production_is_const: false,
        energy: 0,
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "concrete",
        ui_name: "Concrete Mixer",
        description: "Produces concrete, an all-round material for construction. Works well on any planet.",
        ui_category: "production",
        cost: () => ({ worker: 10, concrete: 60, metal: 70, plastic: 20 }),
        time: () => (0.8),
        points: (l) => l * 1,
        energy: 5,
        production: (l) => ({ concrete: 10 }),
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "metal",
        ui_name: "Metal Mine",
        description: "Produces metal, which is an essential resource for construction of buildings and ships. While almost every planet has metal ore deposits the amount and therefore the efficiency of the metal mine varies.",
        ui_category: "production",
        cost: () => ({ worker: 10, concrete: 80, metal: 50, plastic: 20 }),
        time: () => (0.9),
        points: (l) => l * 1,
        energy: 2,
        production: (l) => ({ metal: 10 }),
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "plastic",
        ui_name: "Polymers Assembler",
        description: "Assembles various kinds of polymers for whichever project they are needed. Especially important for research facilities, ships and fuel production.",
        ui_category: "production",
        cost: () => ({ worker: 10, concrete: 50, metal: 50, plastic: 40 }),
        time: () => (1.2),
        points: (l) => l * 1,
        energy: 8,
        production: (l) => ({ plastic: 10 }),
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "fuel",
        ui_name: "Fuel Refinery",
        description: "Refines polymers into material fit for combustion. Works fine in any planetary environment.",
        ui_category: "production",
        cost: l => ({ worker: 5, concrete: 35, metal: 29, plastic: 37 }),
        time: () => (1.3),
        points: (l) => l * 1,
        energy: 10,
        production: (l) => ({ fuel: 10 }),
        consumption: (l) => ({ plastic: 5 }),
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "storage",
        ui_name: "Resource Depot",
        description: "Stores resources, in huge underground facilities to shield them from possible attacks and exposure to the elements. A higher level increases the amount of available storage.",
        ui_category: "infrastructure",
        cost: () => ({ concrete: 25, metal: 10, plastic: 10 }),
        time: () => (0.8),
        points: (l) => l * 1,
        energy: 0.8,
        requirements: [{ id: "central", level: 1 }],
        capacity: (l) => 1000 + 500 * Math.pow(l, 2),
    }, {
        id: "space_port",
        ui_name: "Space Port",
        description: "This planets gate into space. It is used to load and unload cargo from ships. To efficiently handle large amounts of cargo, it should be upgraded frequently.",
        ui_category: "infrastructure",
        cost: l => ({ worker: 30, concrete: 10, metal: 10, plastic: 5 }),
        time: () => (1.0),
        points: (l) => l * 2,
        energy: 10,
        requirements: [],
    }, {
        id: "lab",
        ui_name: "Research Lab",
        description: "A place for science and knowledge. It is a primary resource in advancing your empire by discovering new technologies and improving existing ones. Every research lab increases the number of available scientists, which in turn improve the research speed.",
        ui_category: "infrastructure",
        cost: l => ({ worker: 20, concrete: 60, metal: 40, plastic: 60 }),
        time: () => (0.8),
        time_level_offset: () => (1),
        points: (l) => l * 2,
        energy: 15,
        cost_level_offset: 4,
        requirements: [{ id: "central", level: 1 }],
    }, {
        id: "ship_factory",
        ui_name: "Shipyard",
        description: "Responsible for constructing and designing star ships. It is essential for building a strong fleet -- be it trading or fighting.",
        ui_category: "infrastructure",
        cost: l => ({ worker: 20, concrete: 55, metal: 55, plastic: 40 }),
        time: () => (1.2),
        time_level_offset: () => (1),
        points: (l) => l * 2,
        energy: 15,
        cost_level_offset: 4,
        requirements: [{ id: "central", level: 1 }, { id: "lab", level: 1 }],
    }];

const TECHNOLOGIES = [
    {
        id: "astronomy",
        category: "special",
        max_level: 1,
        ui_name: "Astronomy",
        description: "Unlocks the galaxy browser.",
        research_cost: 0.5,
        research_time: 1.0,
    }, {
        id: "body",
        category: "ship",
        ui_name: "Ship Hull",
        description: "Enables larger and larger ships. Each researched level of this technology allows to equip five additional modules.",
        research_cost: 0.9,
        research_time: 0.9,
        build_cost: { worker: 5, metal: 80, plastic: 60 },
        build_time: 3.0,
        properties: (l) => ({ structure: 5 * l }),
    }, {
        id: "combustion",
        category: "ship",
        ui_name: "Combustion Drive",
        description: "Using combustion-based rocket engines to accelerate a space craft has been around for a while. It consumes a lot of fuel and is rather slow. However, the technology is simple and cheap to build and the short-term acceleration provides great maneuverability is in a battle. When combined with a space-fold core it can be used to travel throughout the stars. Otherwise, it is utterly useless to get beyond your solar system.",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 1, metal: 40, plastic: 40 },
        build_time: 1.0,
        properties: (l) => ({ thrust: Math.pow(l + 2, 1.2) + 30 * l, structure: 3 * l }),
    }, {
        id: "ion",
        category: "ship",
        ui_name: "Ion Drive",
        description: "?",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 1, metal: 40, plastic: 40 },
        build_time: 1.0,
        properties: (l) => ({ thrust: Math.pow(8.0, l), structure: 5 * l }),
    }, {
        id: "spacefold",
        category: "ship",
        ui_name: "Space-fold Core",
        description: "This is the key technology to unlock the far reaches of the universe, or more precisely anything beyond your solar system. It manipulates the space around a ship and allows it to move faster than the speed of light. The inventor, Dr. Farley, described it as follows: \"With conventional travel, it is like paddling up a fast flowing river. The space-fold core envelops the entire ship and allows it to flow with the stream.\".",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 1, metal: 40, plastic: 40 },
        build_time: 1.0,
        properties: (l) => ({ thrust: Math.pow(100.0, l), structure: 10 * l }),
    }, {
        id: "storage",
        category: "ship",
        ui_name: "Cargo Storage",
        description: "This is an entire field, reaching from the logistics of optimizing the available space in a ship, over the construction of larger cargo bays all the way to quantum compression of the cargo itself. Using this the storage capacity for resources in space crafts can be increased.",
        research_cost: 0.7,
        research_time: 0.8,
        build_cost: { metal: 40 },
        build_time: 0.5,
        properties: (l) => ({ cargo: Math.pow(l, 1.1) + 100 * l, structure: 2 * l }),
    }, {
        id: "projectile",
        category: "ship",
        ui_name: "Projectile Weapons",
        description: "While the concept is not new, the principle works better than ever: We simply launch as much fast-moving mass in the direction of the enemy as fast as possible. With the absence of friction this weapon is deadlier than ever. The research is about fining optimizing the compounds and shapes and most importantly about accelerating them.",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 2, metal: 40, plastic: 20 },
        build_time: 1.0,
        properties: (l) => ({ attack: Math.pow(l, 1.2) + 8 * l, structure: 5 * l }),
    }, {
        id: "beam",
        category: "ship",
        ui_name: "Beam Weapons",
        description: "?",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 2, metal: 40, plastic: 20 },
        build_time: 1.0,
        properties: (l) => ({ attack: Math.pow(10.0, l), structure: 3 * l }),
    }, {
        id: "wave",
        category: "ship",
        ui_name: "Wave Weapons",
        description: "?",
        research_cost: 1.0,
        research_time: 1.0,
        build_cost: { worker: 2, metal: 40, plastic: 20 },
        build_time: 1.0,
        properties: (l) => ({ attack: Math.pow(15.0, l), structure: 3 * l }),
    }, {
        id: "stealth",
        category: "ship",
        ui_name: "Stealth & Sensors",
        description: "This line of research is an on-going arms race between stealth and sensors technology. An improvement in one field often triggers and advancement in the other field. A factions capability of cloaking their ships is never far behind that of locating the same ones. Spending resources on advancing the field allows you to cloak your ships better and detect incoming ships as.",
        research_cost: 0,
        research_time: 0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({}),
    }, {
        id: "recycle",
        category: "ship",
        ui_name: "Recycling",
        description: "?",
        research_cost: 0,
        research_time: 0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({}),
    }, {
        id: "raid",
        category: "ship",
        ui_name: "Raiding",
        description: "?",
        research_cost: 0,
        research_time: 0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({}),
    }, {
        id: "armor",
        category: "ship",
        ui_name: "Armor",
        description: "Ever since space ships have been used for battle, it became necessary to protect them. Slapping on more material to the hull is an effective way of doing do. However, it costs a lot of resources and makes the ship heavier.",
        research_cost: 0.8,
        research_time: 0.8,
        build_cost: { metal: 60 },
        build_time: 1.2,
        properties: (l) => ({ armor: Math.pow(5.0, l), structure: 25 * l }),
    }, {
        id: "shield",
        category: "ship",
        ui_name: "Shield",
        description: "?",
        research_cost: 0.0,
        research_time: 0.0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({ shield: Math.pow(5.0, l), structure: 3 * l }),
    }, {
        id: "colo",
        category: "ship",
        ui_name: "Colonization",
        description: "The study of methods that allows you civilization to inhabit other planets. It is crude in the beginning, allowing you only to colonize planets similar to your home world. But with advancements in this research other planet types will become available.",
        research_cost: 2.2,
        research_time: 1.0,
        build_cost: { worker: 250, metal: 150, plastic: 100 },
        build_time: 4.0,
        properties: (l) => ({ structure: 10 * l }),
    }, {
        id: "repair",
        category: "ship",
        ui_name: "Repair",
        description: "Improves the ship qualitity to repair itself and other ships in the fleet.",
        research_cost: 0.0,
        research_time: 0.0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({ structure: 1 * l, repair: 10 * l }),
    }, {
        id: "inva",
        category: "ship",
        ui_name: "Invasion",
        description: "",
        research_cost: 0.0,
        research_time: 0.0,
        build_cost: {},
        build_time: 0,
        properties: (l) => ({}),
    }];

const SHIP_STATS = [
    {
        id: "attack",
        ui_name: "Attack",
    }, {
        id: "structure",
        ui_name: "Structure",
    }, {
        id: "armor",
        ui_name: "Armor",
    }, {
        id: "shield",
        ui_name: "Shield",
    }, {
        id: "thrust",
        ui_name: "Thrust",
    }, {
        id: "cargo",
        ui_name: "Cargo",
    },
];

const FLEET_STEPS = {
    CREATED: {
        subject: "Fleet Created",
        content: "#fleet was just created. It will load resources and launch now.",
    },
    MOVE_COMPLETE: {
        subject: "Fleet Arrived",
        content: "#fleet arrived at designated location: #planet.",
    },
    MOVE_NO_FUEL: {
        subject: "FAILED: Fleet Move",
        content: "#fleet could not start moving, there is not enough fuel.",
    },
    LAND_SUCCESS: {
        subject: "Fleet Landed",
        content: "#fleet successfully landed on #planet.",
    },
    LOAD_FAIL_FOREIGN_PLANET: {
        subject: "Loading Failed",
        content: "#fleet can not load resources from foreign planet: #planet.",
    },
    LOAD_DONE: {
        subject: "Loading Done",
        content: "#fleet finished loading resources.",
    },
    LAUNCH_FAIL_FOREIGN_PLANET: {
        subject: "Fleet Launch Failed",
        content: "#fleet did not have enough resources to launch from #planet. Because the planet does not belong to you, it was destroyed.",
    },
    LAUNCH_FAIL_MY_PLANET: {
        subject: "Fleet Launch Failed",
        content: "#fleet did not have enough resources to launch. It will now unload its resources and be stationed on #planet.",
    },
    LAUNCH_SUCCESS: {
        subject: "Fleet Launched",
        content: "#fleet successfully launched from #planet.",
    },
    COLONIZE_SUCCESS: {
        subject: "Colonization Successful",
        content: "#fleet colonized #planet successfully. The colonization ships were consumed and the others were stationed on the planet.",
    },
    COLONIZE_OCCUPIED: {
        subject: "Failed: Colonization",
        content: "#fleet failed colonizing #planet. The planet is already occupied.",
    },
    STATION_DONE: {
        subject: "Fleet Stationed",
        content: "#fleet has been stationed on the #planet.",
    },
    STATION_FAIL_FOREIGN_OWNER: {
        subject: "FAILED: Station Fleet",
        content: "#fleet could not be stationed on #planet!",
    },
    COMBAT_TICK_REPORT: {
        subject: "Combat Update",
        content: "#fleet fought on #planet. #combat-report",
    },
    RAID_FAIL_OWN_PLANET: {
        subject: "Failed: Raid",
        content: "#fleet can not raid your own planet: #planet!",
    },
    REPAIR_DONE: {
        subject: "Repair Complete",
        content: "#fleet is fully repaired! #repaired_structure was recovered.",
    },
    RAID_DONE: {
        subject: "Raid Success",
        content: "#fleet has raided #planet! The following resources where obtained: #resources",
    },
};

const SHIP_ROLES = [
    {
        id: "normal",
        ui_name: "Normal",
        description: "The ship behaves normally in combat.",
    }, {
        id: "tank",
        ui_name: "Tank",
        description: "The ship tries to draw fire, making it more likely to get hit.",
    }
];

class GameDataClass {
    constructor() {
        this.RESOURCES = RESOURCES;
        this.BUILDINGS = BUILDINGS;
        this.TECHNOLOGIES = TECHNOLOGIES;
        this.SHIP_STATS = SHIP_STATS;
        this.FLEET_STEPS = FLEET_STEPS;
        this.SHIP_ROLES = SHIP_ROLES;
        this.SPEED = SPEED;
        this.SECOND = SECOND;
        this.MINUTE = MINUTE;
        this.HOUR = HOUR;
        this.DAY = DAY;
        this.YEAR = YEAR;

        this.TECHNOLOGY_BY_ID = this.TECHNOLOGIES.reduce((map, obj) => {
            map[obj.id] = obj;
            return map;
        }, {});
        this.BUILDING_BY_ID = this.BUILDINGS.reduce((map, obj) => {
            map[obj.id] = obj;
            return map;
        }, {});
        this.RESOURCE_BY_ID = this.RESOURCES.reduce((map, obj) => {
            map[obj.id] = obj;
            return map;
        }, {});
        this.PRODUCTION_BUILDINGS = this.BUILDINGS.reduce((arr, obj) => {
            if (obj.production) arr.push(obj);
            return arr;
        }, []);
        this.WORKER_PRODUCTION_BUILDINGS = this.BUILDINGS.reduce((arr, obj) => {
            if (obj.worker_production_const || obj.worker_replication_factor) arr.push(obj);
            return arr;
        }, []);
        this.CONSUMPTION_BUILDINGS = this.BUILDINGS.reduce((arr, obj) => {
            if (obj.consumption) arr.push(obj);
            return arr;
        }, []);
        this.SHIP_TECHNOLOGIES = this.TECHNOLOGIES.reduce((arr, obj) => {
            if (obj.category === "ship") arr.push(obj);
            return arr;
        }, []);
        this.SHIP_ROLE_BY_ID = this.SHIP_ROLES.reduce((map, obj) => {
            map[obj.id] = obj;
            return map;
        }, {});
    }

    resName(resource_id) {
        return this.RESOURCE_BY_ID[resource_id].ui_name;
    }

    buildingName(building_id) {
        return this.BUILDING_BY_ID[building_id].ui_name;
    }

    techName(technology_id) {
        return this.TECHNOLOGY_BY_ID[technology_id].ui_name;
    }

    getEmptyResourceVector() {
        return new ResourceVector().init();
    }
}

let GameData = new GameDataClass();

export { GameData, Timing, CostProductionFormulas };
